import { createSelector } from '@reduxjs/toolkit';
import { constants } from 'config/constants';
import { withoutLockers } from 'library/helpers/accessProductDevices';
import { sortLockersNaturalWay } from 'library/helpers/lockerHelpers';
import { isLockerDevice } from 'library/helpers/deviceFamily';
import { selectCategoryFieldsByCategoryID } from 'reduxStore/categories/selectors';
import { selectSelectedSiteID, selectIsPortfolioMode } from 'reduxStore/app/selectors';

/**
 * Base input selectors for scoped devices data.
 * Bucket path: state.Devices.data[orgID][siteID].data
 */
const selectDevicesData = (state) => state.Devices.data;
const selectOrgID = (state) => state.Auth.selectedProfile.orgID;
const selectSiteID = (state) => state.Auth.selectedProfile.siteID;
const selectUserRoles = (state) => state.Auth.selectedProfile?.roles || {};

//📌 Selector for get all devices from current profile — sorted A-Z by name
export const selectDevices = createSelector(
	[selectDevicesData, selectOrgID, selectSiteID],
	(devicesData, orgID, siteID) => {
		const key = siteID;
		return [...(devicesData[orgID]?.[key]?.data || [])].sort((a, b) =>
			(a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
		);
	}
);

/**
 * 📌 The active scope bucket's PROGRESS — not its rows.
 *
 * Two answers an empty array cannot give: is /devices still in flight, and has
 * getAllDevicesAllLocations placed the rows yet. The second is the one that is easy to
 * miss — `loading` is already false while the rows are still unplaced. The Cameras page
 * derives its whole list from this bucket and needs both; see reduxStore/cameras/selectors.
 *
 * ⚠️ An ABSENT bucket reports `locationsLoaded: false`, so consumers read an unread scope
 * as still loading rather than as empty. Nothing can sit on that: `onScopeChange` fetches
 * devices for every scope unconditionally, and the shell will not mount a route until the
 * bucket has landed.
 */
const ABSENT_BUCKET = Object.freeze({ loading: false, locationsLoaded: false });

export const selectDevicesBucketState = createSelector(
	[selectDevicesData, selectOrgID, selectSiteID],
	(devicesData, orgID, siteID) => {
		const bucket = devicesData[orgID]?.[siteID];
		if (!bucket) return ABSENT_BUCKET;
		return { loading: bucket.loading === true, locationsLoaded: bucket.locationsLoaded === true };
	},
	// The object is rebuilt on every devices write; without this every consumer re-renders
	// on any device patch, which on the Cameras page means the whole wall.
	{
		memoizeOptions: {
			resultEqualityCheck: (a, b) => a.loading === b.loading && a.locationsLoaded === b.locationsLoaded
		}
	}
);

//📌 Selector: loading state for the active scope bucket
export const selectDevicesLoading = createSelector(
	[selectDevicesData, selectOrgID, selectSiteID],
	(devicesData, orgID, siteID) => {
		const key = siteID;
		return devicesData[orgID]?.[key]?.loading ?? false;
	}
);

//📌 Whether the list selectLockersDevices reads has landed. Not selectDevicesLoading:
// that one follows the active scope, and Lockers always reads the 'all' bucket.
export const selectLockersDevicesReady = createSelector([selectDevicesData, selectOrgID], (devicesData, orgID) => {
	const bucket = devicesData[orgID]?.['all'];
	return Boolean(bucket) && !bucket.loading;
});

//📌 Selector for get device by deviceID — searches active bucket, falls back to 'all'
export const selectDevicesByDeviceID = createSelector(
	[selectDevicesData, selectOrgID, selectSiteID, (_, deviceID) => deviceID],
	(devicesData, orgID, siteID, deviceID) => {
		const key = siteID;
		const found = devicesData[orgID]?.[key]?.data?.find((d) => d.deviceID === deviceID);
		if (found) return found;
		// Fallback: search 'all' bucket
		if (key !== 'all') {
			return devicesData[orgID]?.['all']?.data?.find((d) => d.deviceID === deviceID) || null;
		}
		return null;
	}
);

//📌 Selector for get all fields by deviceID.
export const selectDeviceFieldsByDeviceID = createSelector(
	[selectDevices, (_, deviceID) => deviceID],
	(devices, deviceID) => devices.find((device) => device.deviceID === deviceID)?.fields || []
);

//📌 Selector for get all available fields to add to device by deviceID and categoryID.
export const selectAvailableCategoryFieldsToAdd = (deviceID, categoryID) =>
	createSelector(
		(state) => selectDeviceFieldsByDeviceID(state, deviceID),
		(state) => selectCategoryFieldsByCategoryID(state, categoryID),
		(deviceFields, categoryFields) => {
			if (!deviceFields || !categoryFields) return [];
			return categoryFields.filter(
				(categoryField) => !deviceFields.some((df) => df.fieldID === categoryField.fieldID)
			);
		}
	);

//📌 Selector for get all category fields (for modal editing)
export const selectAllCategoryFields = (categoryID) =>
	createSelector(
		(state) => selectCategoryFieldsByCategoryID(state, categoryID),
		(categoryFields) => categoryFields || []
	);

//📌 Selector for get all devices by categoryID
export const selectDevicesByCategoryID = createSelector(
	[selectDevices, (_, categoryID) => categoryID],
	(devices, categoryID) => devices.filter((device) => device.categoryID === categoryID) || []
);

//📌 Selector for get all devices by name.
export const selectDevicesByName = createSelector(
	[selectDevices, (_, name) => name],
	(devices, name) => devices.filter((device) => device.name.toLowerCase().includes(name.toLowerCase())) || []
);

//📌 Selector for get all devices by defaultCategoryID (Lockers).
export const selectLockersDevices = createSelector([selectDevicesData, selectOrgID], (devicesData, orgID) => {
	// Lockers always reads from 'all' bucket
	const allDevices = devicesData[orgID]?.['all']?.data || [];

	const bookableLockers = allDevices.filter(isLockerDevice);

	// 🔀 Apply natural sorting
	return sortLockersNaturalWay(bookableLockers);
});

//📌 Selector: devices that can grant access — TTLock/Zezamii digital devices + IR hardware
// + Inception doors/lift-floors. Gateways, cameras, assets and Inception areas are excluded:
// they are never permission targets and never belong in access groups (types 2/3).
export const selectAccessGrantingDevices = createSelector([selectDevices], (devices) =>
	// withoutLockers because BOOKEABLE_LOCKERS (14/19) sits inside CONTROL_DEVICE_CATEGORIES:
	// a locker is reached through a Lockers booking, never an Access permission.
	withoutLockers(devices).filter(
		(d) =>
			constants.CONTROL_DEVICE_CATEGORIES.includes(d.defaultCategoryID) ||
			constants.INNER_RANGE_DEVICE_CATEGORIES.includes(d.defaultCategoryID) ||
			constants.INCEPTION_DEVICE_CATEGORIES.includes(d.defaultCategoryID)
	)
);

//📌 Selector: device IDs a PERMISSION can never reach, even when they sit inside a granted
// access group. Cameras are the case that exists: they are not a grantable resource (ADR-0007)
// and the server drops them during grant expansion, so any permission view that derives its
// device set from the GROUP STRUCTURE (rather than from real permission rows) must subtract
// this set — otherwise it renders access the grant never created.
// Group MEMBERSHIP views must NOT use this: a camera really is a member of its group.
export const selectNonGrantableDeviceIDs = createSelector(
	[selectDevices],
	(devices) =>
		new Set(devices.filter((d) => constants.CAMERA_CATEGORIES.includes(d.defaultCategoryID)).map((d) => d.deviceID))
);

//📌 Selector: devices that belong in a group of the given type (Device.GroupType).
// General → assets, cameras, gateways only (never grants access).
// Every OTHER type (Access Control, Inner Range, Inception, Akuvox, …) → any
// access-granting device, mixed freely: Zezamii/Control locks (TTLock),
// Inner Range and Inception (ACS_DEVICE_CATEGORIES). Keeping access-granting
// devices out of General groups prevents mixing assets with digital devices,
// since groups are used as permission targets.
export const selectDevicesByGroupType = createSelector([selectDevices, (_, typeID) => typeID], (devices, typeID) => {
	if (!typeID) return []; // no type selected yet
	return devices.filter((d) => {
		const catID = d.defaultCategoryID;
		const isAccessCategory =
			constants.CONTROL_DEVICE_CATEGORIES.includes(catID) || constants.ACS_DEVICE_CATEGORIES.includes(catID);
		// General shows non-access devices; any other type shows access devices.
		return typeID === constants.GENERAL ? !isAccessCategory : isAccessCategory;
	});
});

//📌 Devices a lock/unlock command can target — the source for both control modals
// (ControlDeviceModal + ControlEmergencyModal).
//
// Category only: `selectDevices` is ALREADY scoped to the active site by the backend
// (`/devices?siteID=`), so re-filtering here on `location[0].siteID` was both redundant and
// wrong. `location` is patched in by a SECOND request (loadAllDevicesLocations) and is only
// set for rows with a floorID, so that filter dropped every device not placed on a floor
// plan — and dropped ALL of them in the window before that request lands.
export const selectUnlockableDevices = createSelector([selectDevices], (devices) =>
	devices.filter((device) => constants.UNLOCK_CATEGORIES.includes(device.defaultCategoryID))
);

//📌 Selector for site-scoped devices.
// With scoped architecture, selectDevices already returns site-filtered data.
// This selector is kept for backwards compatibility but is now a passthrough.
export const selectDevicesBySiteID = createSelector(
	[selectDevices, (_, siteID) => siteID],
	(devices, siteID) => {
		if (siteID === 'all') return devices;
		return devices.filter((device) => {
			if (Array.isArray(device.sites) && device.sites.length > 0) {
				return device.sites.some((s) => (s.siteID ?? s) === siteID);
			}
			if (Array.isArray(device.location) && device.location.length > 0) {
				return device.location.some((loc) => loc.siteID === siteID);
			}
			return false;
		});
	},
	// Null siteID is intentional portfolio-mode passthrough; reselect's identity
	// check would flag the early return as a degenerate selector.
	{ devModeChecks: { identityFunctionCheck: 'never' } }
);

//📌 Selector for get all Control integration devices (locks only).
export const selectControlDevices = createSelector([selectDevices], (devices) => {
	return devices.filter((device) => constants.CONTROL_DEVICE_CATEGORIES.includes(device.defaultCategoryID));
});

// 📌 Cloud Key WS1: devices a visitor cloud key can remote-unlock — scoped by
// unlock CAPABILITY (`UNLOCK_CATEGORIES` = Control + TTLock-gateway + IR doors),
// NOT the Control integration. Multi-select-friendly; "permitted"/"reachable"
// are enforced server-side.
//
// Lifts/cameras are EXPLICITLY excluded — a visitor key must never target one
// even if `UNLOCK_CATEGORIES` is later broadened (the server unlock-adapter
// registry only actuates Control/TTLock gateway + IR doors). Don't rely on
// UNLOCK_CATEGORIES membership alone: it currently includes IR lifts (21).
const CLOUD_KEY_EXCLUDED_CATEGORIES = [
	constants.INNER_RANGE_LIFTS,
	constants.INNER_RANGE_LIFTS_FLOORS,
	constants.VERKADA_CAMERAS,
	constants.VORTEX_CAMERAS
];
export const selectRemoteUnlockDevices = createSelector([selectDevices], (devices) =>
	devices.filter(
		(device) =>
			constants.UNLOCK_CATEGORIES.includes(device.defaultCategoryID) &&
			!CLOUD_KEY_EXCLUDED_CATEGORIES.includes(device.defaultCategoryID)
	)
);

//📌 Selector: Lockers — always reads org-wide devices from 'all' bucket
export const selectLockersAllDevices = createSelector(
	[selectDevicesData, selectOrgID],
	(devicesData, orgID) => devicesData[orgID]?.['all']?.data || []
);

/**
 * 📌 The locker product's device scope: bookable lockers + the org's gateways
 * (not narrowed to which gateway serves which locker — the expandable gateway
 * panel already lists bound locks).
 *
 * Composed off selectLockersAllDevices, whose UNFILTERED result is what leaked
 * cameras, access points, unmanaged Control rows and generic assets onto the
 * lockers Devices page. That selector stays as-is: it's also aliased as
 * selectAllSitesDevices for the cross-site person/visitor pickers, which
 * legitimately need every device.
 */
export const selectLockersPageDevices = createSelector([selectLockersAllDevices], (devices) =>
	sortLockersNaturalWay(
		devices.filter(
			(device) =>
				constants.BOOKEABLE_LOCKERS.includes(device.defaultCategoryID) ||
				constants.GATEWAY_CATEGORIES.includes(device.defaultCategoryID)
		)
	)
);

//📌 Cross-site selectors: read the org-wide ('all') bucket regardless of the top-bar site.
// Used by Add Visitor / Add Person so the issuer can pick any permitted site's doors
// without changing the dashboard's site scope. Requires the 'all' bucket to be loaded.
export const selectAllSitesDevices = selectLockersAllDevices;

export const selectAllSitesRemoteUnlockDevices = createSelector([selectLockersAllDevices], (devices) =>
	devices.filter((device) => constants.UNLOCK_CATEGORIES.includes(device.defaultCategoryID))
);

export const selectAllSitesDevicesLoading = createSelector(
	[selectDevicesData, selectOrgID],
	(devicesData, orgID) => devicesData[orgID]?.['all']?.loading ?? false
);

//📌 Selector: Map<deviceID, name> for O(1) name lookups
export const selectDevicesNameMap = createSelector([selectDevices], (devices) => {
	const map = new Map();
	devices.forEach((d) => map.set(d.deviceID, d.name || 'Unknown'));
	return map;
});

/** All devices across all sites for the current org — used for cross-site permission tables */
export const selectAllOrgDevices = createSelector([selectDevicesData, selectOrgID], (devicesData, orgID) => {
	const orgBuckets = devicesData[orgID];
	if (!orgBuckets) return [];
	const seen = new Set();
	const all = [];
	Object.values(orgBuckets).forEach((bucket) => {
		(bucket?.data || []).forEach((d) => {
			if (!seen.has(d.deviceID)) {
				seen.add(d.deviceID);
				all.push(d);
			}
		});
	});
	return all;
});

export const selectCopyPreview = (state) => state.Devices.copyPreview;
export const selectCopyPreviewLoading = (state) => state.Devices.copyPreviewLoading;
export const selectCopyResult = (state) => state.Devices.copyResult;
export const selectCopyDeviceLoading = (state) => state.Devices.copyDeviceLoading;

/**
 * selectIsDeviceOwnerInCurrentSite(state, deviceID) → boolean
 *
 * Returns true when the device has an owner row (floorID NOT NULL) in the
 * admin's selected site, OR when the admin is in portfolio scope.
 *
 * owner row in my site  → true  (full control: location, site editing, visibility)
 * only visibility row   → false (read-only: no location tab, no site editing)
 * portfolio scope       → true  (global admin, full control)
 */
export const selectIsDeviceOwnerInCurrentSite = createSelector(
	[selectDevicesByDeviceID, selectSelectedSiteID, selectIsPortfolioMode, selectUserRoles],
	(device, selectedSiteID, isPortfolio, userRoles) => {
		if (isPortfolio) return true;
		// Global admin (101), installer (111) or developer (201) always has full control
		const roleIDs = Object.keys(userRoles).map(Number);
		if (roleIDs.includes(101) || roleIDs.includes(111) || roleIDs.includes(201)) return true;
		if (!device?.location || !selectedSiteID) return false;
		return device.location.some((loc) => loc.floorID != null && loc.siteID === selectedSiteID);
	}
);

//📌 Live check (POST /devices/:deviceID/check-live) — keyed by deviceID. Undefined until
// someone has checked that device this session; the stored fields are what renders before.
export const selectDeviceLive = (state, deviceID) => state.Devices.liveByDeviceID[deviceID];
export const selectDeviceLiveMap = (state) => state.Devices.liveByDeviceID;
export const selectDeviceLiveLoadingMap = (state) => state.Devices.liveLoadingByDeviceID;
export const selectDeviceLiveLoading = (state, deviceID) => !!state.Devices.liveLoadingByDeviceID[deviceID];

//📌 The bulk live check, by the caller's batch handle (a gateway's deviceID today).
// `{ loading, checkedAt }` — checkedAt is what the caller's cooldown counts from.

//📌 Connectivity history for ONE device — `buckets` for the strip, `records` + `nextBefore`
// for the timeline. Mirrors the Gateways slice's shape; it is the same table one level down.
export const selectDeviceHistory = (state, deviceID) => state.Devices.historyByDeviceID[deviceID];
export const selectDeviceHistoryMap = (state) => state.Devices.historyByDeviceID;
// 'replace' | 'append' | false — the feed dims on one and spins on the other.
export const selectDeviceHistoryLoading = (state, deviceID) => state.Devices.historyByDeviceID[deviceID]?.loading;
